import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ExternalLink, FileText, Search } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { card, pageHeading, pageSubtitle, theme, type as text } from "../../theme";
import { MarkdownDocument } from "../../components/MarkdownDocument";
import { ShellLink } from "../../components/ShellLink";
import { libraryPath } from "../../lib/playbooks";

// ── Library ─────────────────────────────────────────────────────────────────
// The openly licensed publications the playbooks quote, readable in full. The text is the
// publisher's own, converted from their PDF with only the page layout removed; each document
// names its licence and links to the original. Documents whose terms do not allow us to
// republish them are listed with a link to the publisher instead.

interface LibraryDocument {
  id: string; title: string; publisher: string; edition: string; area: string; summary: string;
  licence: string; licenceLabel: string; sourceUrl: string; readable: boolean; parts: number;
}
interface Area { id: string; label: string }

const linkStyle = { color: theme.color.accentText, textDecoration: "none", fontWeight: 600 } as const;
const inputStyle = {
  width: "100%", padding: "8px 10px 8px 32px", borderRadius: 4, border: `1px solid ${theme.color.border}`,
  background: theme.color.panel, color: theme.color.text, fontSize: 13,
} as const;

function DocRow({ d }: { d: LibraryDocument }) {
  return (
    <div style={{ padding: "10px 16px", borderTop: "1px solid var(--c-border)" }}>
      {d.readable
        ? <ShellLink to={libraryPath(d.id)} style={linkStyle}>{d.title}</ShellLink>
        : <a href={d.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>{d.title} <ExternalLink size={11} /></a>}
      <span style={{ ...text.hint, display: "block", marginTop: 2 }}>{d.summary}</span>
      <span style={{ ...text.hint, display: "block", color: theme.color.faint }}>
        {d.publisher}, {d.edition} · {d.readable ? d.licenceLabel : "Read it on the publisher's site"}
      </span>
    </div>
  );
}

function LibraryList() {
  const [data, setData] = useState<{ areas: Area[]; documents: LibraryDocument[] } | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get<{ areas: Area[]; documents: LibraryDocument[] }>("/help/library")
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ areas: [], documents: [] }); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!data) return [];
    return needle
      ? data.documents.filter((d) => `${d.title} ${d.publisher} ${d.summary}`.toLowerCase().includes(needle))
      : data.documents;
  }, [data, q]);

  return (
    <div style={{ padding: "20px 24px" }}>
      <h1 style={pageHeading}>Library</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 16px" }}>
        The guidance the playbooks quote, from NIST, the NCSC, ENISA, the EDPB, CISA and the HSE — free to read here, with each publisher's licence.
      </p>
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: theme.color.faint }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles and publishers" style={inputStyle} aria-label="Search the library" />
      </div>
      {data === null && <p style={text.hint}>Loading…</p>}
      {data !== null && q.trim() && filtered.length === 0 && <p style={text.hint}>No documents match.</p>}
      {data?.areas.map((area) => {
        const docs = filtered.filter((d) => d.area === area.id);
        if (docs.length === 0) return null;
        return (
          <section key={area.id} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 14 }}>
            <h2 style={{ ...text.sectionTitle, margin: 0, padding: "12px 16px" }}>{area.label}</h2>
            {docs.map((d) => <DocRow key={d.id} d={d} />)}
          </section>
        );
      })}
    </div>
  );
}

function LibraryReader({ id }: { id: string }) {
  const [params, setParams] = useSearchParams();
  const part = Number.parseInt(params.get("part") ?? "0", 10) || 0;
  const [data, setData] = useState<{ document: LibraryDocument; toc: Array<{ index: number; title: string }>; part: { index: number; markdown: string } | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<NonNullable<typeof data>>(`/help/library/${encodeURIComponent(id)}?part=${part}`)
      .then((d) => { if (!cancelled) { setData(d); window.scrollTo?.(0, 0); } })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError && e.status === 404 ? "notfound" : "This document could not be loaded."); });
    return () => { cancelled = true; };
  }, [id, part]);

  if (error) {
    return (
      <div style={{ padding: "20px 24px" }}>
        <ShellLink to="/help/library" style={{ ...text.hint, ...linkStyle }}>← Library</ShellLink>
        <p style={{ ...text.hint, marginTop: 8 }}>{error === "notfound" ? "Document not found." : error}</p>
      </div>
    );
  }
  if (!data) return <div style={{ padding: "20px 24px" }}><p style={text.hint}>Loading…</p></div>;
  const { document: d, toc } = data;

  return (
    <div style={{ padding: "20px 24px" }}>
      <ShellLink to="/help/library" style={{ ...text.hint, ...linkStyle }}>← Library</ShellLink>
      <h1 style={{ ...pageHeading, marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}><FileText size={20} /> {d.title}</h1>
      <p style={{ ...text.hint, margin: "0 0 4px" }}>{d.publisher}, {d.edition} · {d.licenceLabel}</p>
      <p style={{ ...text.hint, margin: "0 0 14px" }}>
        Converted from the publisher's PDF; only page layout was removed. Tables and figures may read better in the{" "}
        <a href={d.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>original <ExternalLink size={11} /></a>.
      </p>
      {toc.length > 1 && (
        <label style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
          Part
          <select
            value={part}
            onChange={(e) => setParams((p) => { const n = new URLSearchParams(p); n.set("part", e.target.value); return n; })}
            style={{ padding: "4px 8px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text }}
          >
            {toc.map((t) => <option key={t.index} value={t.index}>{t.index + 1} of {toc.length} — {t.title.slice(0, 70)}</option>)}
          </select>
        </label>
      )}
      <article style={{ ...card, padding: "16px 20px" }}>
        {data.part
          ? <MarkdownDocument content={data.part.markdown} size={13} />
          : <p style={text.hint}>Read this document on the <a href={d.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>publisher's site</a>.</p>}
      </article>
    </div>
  );
}

export function LibraryPage() {
  const { docId } = useParams<{ docId?: string }>();
  return docId ? <LibraryReader id={docId} /> : <LibraryList />;
}
